import {HistoryEntry} from './history-types';
import {FetchNextPageCompletedAction, SearchCompletedAction, SearchPageAction} from './search-types';
import {
    ProdDocument,
    ProductDocument,
    SearchExportFormat,
    SearchRequest,
    SearchResult,
    SortType,
    TabType,
} from '../search-types';
import {DeepReadonly} from 'utility-types';
import {Dispatch, GetState, State, TempThunkAction} from '../../app-types';
import {ThunkAction} from 'redux-thunk';
import {getActualQueryString, getSearchRequest, isBlacklistedForAutoCorrection, mapDocument} from '../search.utils';
import {doClosePointInTime, doFetchFacets, doFetchNextDocuments, doSearchRequest} from '../../services/search-service';
import {scrollToTop} from '../../utils/utils';
import {SEARCH_RESULTS_LIST_CLASS} from '../search-results/search-result-list/search-results-list';
import {searchPageAttributesMap, SearchPageType} from '../search-config';
import {buildEncodedUrlPath} from '../url-path-builder';
import {ClickableElement} from '../search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {PreviewDocument} from '../search-results/search-preview/doc-preview-section/docPreviewRow-types';
import {PdfHighlight} from '../../common/pdf-viewer/PdfViewer.types';
import {
    fetchHighlightedDocument,
    fetchPdfDocument,
    fetchPlainDocument,
} from '../search-results/search-preview/doc-preview-section/docPreviewRow-service';
import {SpotlightTagType} from '../../config/config-types';
import {NavigateFunction} from 'react-router-dom';
import {QUERY_FIELDS_ALL, QUERY_FIELDS_SEPARATOR} from '../const';
import {DynamicSize} from '../../common/dragable-divider/dragableDivider';
import {DefaultRootState} from 'react-redux';
import {store} from '../../store';
import {showInfo} from '../../message-box/message-box-actions';
import {t} from 'i18next';
import {HibuFacetFilter, HibuFacetGroup} from '../../facets/facet.types';
import {updateAllFilters} from '../../facets/duck/filter.actions';

export const searchHistoryLoadedAction = (
    searchPageType: SearchPageType,
    entries: HistoryEntry[],
): SearchPageAction => {
    return {
        type: 'SEARCH_HISTORY_LOADED',
        searchPageType,
        entries,
    };
};

export const searchHistoryClearedAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        type: 'SEARCH_HISTORY_LOADED',
        searchPageType,
        entries: [],
    };
};

export const searchScrollButtonVisibilityChanged = (
    searchPageType: SearchPageType,
    showScrollButton: boolean,
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_SCROLL_BUTTON_VISIBILITY_CHANGED',
        showScrollButton: showScrollButton,
    };
};

export const searchResultSetExpandedDocumentAction = (
    searchPageType: SearchPageType,
    doc: DeepReadonly<ProdDocument> | undefined,
): SearchPageAction => ({
    searchPageType: searchPageType,
    type: 'SEARCH_RESULT_SET_EXPANDED_DOCUMENT',
    doc: doc,
});

// SEARCH

export const resetSearchStateParts = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'RESET_SEARCH_STATE_PARTS',
    };
};

export const changeInDocumentQueryAction = (inDocumentQuery: string | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SEARCH_CHANGE_IN_DOCUMENT_QUERY',
        inDocumentQuery: inDocumentQuery,
    };
};

export const searchSourceSwitchedAction = (searchPageType: SearchPageType, name: string): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_SOURCE_SWITCHED',
        name: name,
    };
};

export const exportRequestedAction = (
    searchPageType: SearchPageType,
    exportFormat: SearchExportFormat,
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'EXPORT_REQUESTED',
        reportFormat: exportFormat,
    };
};

export const fetchNextPageStartedAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'FETCH_NEXT_PAGE_STARTED',
    };
};

export const sortChangeAction = (searchPageType: SearchPageType, eventKey: SortType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'RESULT_SORT_CHANGED',
        sortType: eventKey,
    };
};

export const startSearchAction = (searchPageType: SearchPageType, query = ''): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_STARTED',
        query: query,
    };
};

export const doneSearchingAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'DONE_SEARCHING_ACTION',
    };
};

export const startSearchChatAction = (
    searchPageType: SearchPageType,
    isChatOpen: boolean,
    currentState: string,
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_CHAT_TRIGGERED',
        isChatOpen: isChatOpen,
        currentState: currentState,
    };
};

export const addBookmarkAction = (searchPageType: SearchPageType, doc: ProdDocument): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_ADD_BOOKMARK',
        doc: doc,
    };
};

export const removeBookmarkAction = (searchPageType: SearchPageType, doc: ProdDocument): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_REMOVE_BOOKMARK',
        doc: doc,
    };
};

export function setSearchQueryFieldsAction(
    searchPageType: SearchPageType,
    containerQueries: string[],
): SearchPageAction {
    return {
        type: 'SET_SEARCH_QUERY_FIELDS',
        searchPageType: searchPageType,
        queryFields: containerQueries,
    };
}

export const searchCompletedAction = (
    searchResult: Partial<SearchResult>,
    searchPageType: SearchPageType,
): SearchCompletedAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_COMPLETED',
        searchResult: searchResult,
    };
};

export const fetchNextPageCompletedAction = (
    docs: ProdDocument[],
    pointInTime?: string,
): FetchNextPageCompletedAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'FETCH_NEXT_PAGE_COMPLETED',
        docs: docs,
        pointInTime: pointInTime,
    };
};

export const clearPointInTimeAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'CLEAR_POINT_IN_TIME',
    };
};

export const expandDocumentDetailsAction = (expanded: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'EXPAND_DOCUMENT_DETAILS',
        expanded: expanded,
    };
};

export const setPreviewDocumentAction = (previewDocument: PreviewDocument | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'PREVIEW_DOCUMENT',
        previewDocument: previewDocument,
    };
};

export const setPreviewDocumentWithHighlightsAction = (
    previewDocumentWithHighlights: PreviewDocument | undefined,
): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'PREVIEW_DOCUMENT_WITH_HIGHLIGHTS',
        previewDocumentWithHighlights: previewDocumentWithHighlights,
    };
};

export const showAnnotations = (showAnnotations: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SHOW_ANNOTATIONS',
        showAnnotations: showAnnotations,
    };
};

export const setDocumentQueryHighlights = (queryHighlights: PdfHighlight[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_DOCUMENT_QUERY_HIGHLIGHTS',
        documentAnnotations: queryHighlights,
    };
};

export const setDocumentSelectedTags = (selectedTags: PdfHighlight[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_DOCUMENT_SELECTED_TAGS',
        documentSelectedTags: selectedTags,
    };
};

export const switchResultDetailsTabAction = (resultDetailsTab: TabType): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SWITCH_RESULT_DETAILS_TAB',
        openResultTab: resultDetailsTab,
    };
};

export const changeResultDetailsTabSizeAction = (size: DynamicSize): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CHANGE_RESULT_DETAILS_TAB_SIZE',
        size: size,
    };
};

export const setQueryFieldsAction = (queryFieldsString: string | null): SearchPageAction => {
    let queryFields: string[] = [];
    const state = store.getState() as DefaultRootState;
    const fieldSpecificSearch = state.settings.fieldSpecificSearch;
    if (queryFieldsString !== null) {
        queryFields = queryFieldsString.split(QUERY_FIELDS_SEPARATOR);
        if (!fieldSpecificSearch) {
            // enable field specific search in user settings
            store.dispatch({type: 'FIELD_SPECIFIC_SEARCH_SET', fieldSpecificSearch: true});
            // show info message that field specific search has been enabled automatically
            store.dispatch(
                showInfo(
                    SearchPageType.BASIC,
                    t('search.info.fieldSpecificSearchEnabled.message'),
                    t('search.info.fieldSpecificSearchEnabled.title'),
                    4000,
                ),
            );
        }
    } else if (fieldSpecificSearch) {
        queryFields = [QUERY_FIELDS_ALL]; // show all if no URL parameter present, but field specific search enabled
    }
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_SEARCH_QUERY_FIELDS',
        queryFields: queryFields,
    };
};

export const setCurrentPageNumberAction = (newPageNumber: number | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_DOCUMENT_PAGE_CHANGED',
        newPageNumber: newPageNumber,
    };
};

export const setCurrentPageRotationAction = (newRotation: number): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CURRENT_PAGE_ROTATED',
        rotation: newRotation,
    };
};

export const setDocumentAllPagesRotation = (rotations: number[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CURRENT_DOCUMENT_ROTATIONS',
        rotations: rotations,
    };
};

export const setScrollTargetAction = (scrollTarget: ClickableElement): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SCROLL_TARGET_CHANGED',
        newPageNumber: scrollTarget.pageNumber,
        scrollTarget: scrollTarget,
    };
};

export const resetTransformAction = (resetTransform: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'RESET_TRANSFORM',
        resetTransform: resetTransform,
    };
};

export const setHighlightIdAction = (highlightId: string | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_HIGHLIGHT_ID',
        highlightId: highlightId,
    };
};

export const setSelectedDocumentNumberOfPages = (numPages: number): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_DOCUMENT_CHANGED',
        numberOfPages: numPages,
    };
};

export const setUseAutoCorrection = (useAutoCorrection: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'UPDATE_AUTO_CORRECTION',
        useAutoCorrection: useAutoCorrection,
    };
};

export function setSortSettingsAction(sortTypes: SortType[]): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_SORT_SETTINGS',
        sortTypes: sortTypes,
    };
}

export function setIsLoadingDocumentAction(isLoading: boolean): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'IS_LOADING_DOCUMENT',
        isLoading: isLoading,
    };
}

export const doFetchDocumentPreview = (
    selectedDocument: ProductDocument | undefined,
    showAnnotations: boolean,
    resultFor: string,
    query: string,
    settingsContainerName: string,
    spotlightTagTypes: SpotlightTagType[],
) => {
    return (dispatch: Dispatch): void => {
        if (selectedDocument) {
            dispatch(setIsLoadingDocumentAction(true));
            const previewLink = selectedDocument.metadata.previewLink_string;
            if (showAnnotations) {
                fetchHighlightedDocument(
                    selectedDocument.id,
                    resultFor || query,
                    settingsContainerName,
                    true,
                    spotlightTagTypes,
                    previewLink,
                    selectedDocument.metadata.contentType_string,
                )
                    .then((fetchDocument) => dispatch(setPreviewDocumentWithHighlightsAction(fetchDocument)))
                    .finally(() => dispatch(setIsLoadingDocumentAction(false)));
            } else if (query) {
                fetchHighlightedDocument(
                    selectedDocument.id,
                    resultFor || query,
                    settingsContainerName,
                    false,
                    [],
                    previewLink,
                    selectedDocument.metadata.contentType_string,
                )
                    .then((fetchDocument) => dispatch(setPreviewDocumentAction(fetchDocument)))
                    .finally(() => dispatch(setIsLoadingDocumentAction(false)));
            } else {
                fetchPdfDocument(previewLink)
                    .then((fetchDocument) => dispatch(setPreviewDocumentAction(fetchDocument)))
                    .finally(() => dispatch(setIsLoadingDocumentAction(false)));
            }
        }
    };
};

export const openNewDocument = (doc: ProdDocument | undefined) => {
    return (dispatch: Dispatch): void => {
        dispatch(setPreviewDocumentAction(undefined));
        dispatch(setPreviewDocumentWithHighlightsAction(undefined));
        dispatch(setCurrentPageNumberAction(1));
        dispatch(setDocumentSelectedTags([]));
        dispatch(fetchDocumentDetails(doc));
    };
};

export const fetchDocumentDetails = (sourceDoc: ProdDocument | undefined) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        const searchPageType = SearchPageType.BASIC;
        const settingsContainerName = getState().settings.containerName;
        if (sourceDoc) {
            dispatch(setIsLoadingDocumentAction(true));
            fetchPlainDocument(searchPageType, sourceDoc.id, settingsContainerName).then((doc) => {
                if (doc) {
                    const productDocument = mapDocument(doc, [], sourceDoc.duplicates);
                    dispatch(searchResultSetExpandedDocumentAction(searchPageType, productDocument));
                    dispatch(setIsLoadingDocumentAction(false));
                }
            });
        }
    };
};

export const fetchNextPage = (searchPageType: SearchPageType, useAutoCorrection: boolean): TempThunkAction => {
    return (dispatch: Dispatch, getState: GetState) => {
        const request: SearchRequest = getSearchRequest(getState, undefined, useAutoCorrection);
        dispatch(fetchNextPageStartedAction(searchPageType));
        dispatch(setUseAutoCorrection(useAutoCorrection));
        return dispatch(doFetchNextDocuments(request));
    };
};

export const sortResults = (searchPageType: SearchPageType, eventKey: SortType, navigate: NavigateFunction) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const queryString: string = getActualQueryString(getState().search[searchPageType]);
        dispatch(sortChangeAction(searchPageType, eventKey));
        return dispatch(search(navigate, queryString, false, false));
    };
};

export const fetchFacets = (query?: string, isUsingAutoCorrection = true) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const searchPageType: SearchPageType = SearchPageType.BASIC;
        const request: SearchRequest = {
            ...getSearchRequest(getState, query, isUsingAutoCorrection),
            pointInTime: undefined,
        };
        return dispatch(doFetchFacets(searchPageType, request));
    };
};

export const search = (
    navigate: NavigateFunction,
    query?: string,
    isSubmittingNewQuery = true,
    isUsingAutoCorrection = true,
): TempThunkAction => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const searchPageType: SearchPageType = SearchPageType.BASIC;
        query = query ?? getState().search[searchPageType].query;
        const pitId = getState().search[searchPageType].pointInTime;
        if (pitId) {
            dispatch(doClosePointInTime(pitId));
        }
        scrollToTop(SEARCH_RESULTS_LIST_CLASS);
        dispatch(startSearchAction(searchPageType, query));

        const request: SearchRequest = getSearchRequest(getState, query, isUsingAutoCorrection);
        const useAutoCorrection = shouldUseAutoCorrection(isUsingAutoCorrection, query);
        dispatch(setUseAutoCorrection(useAutoCorrection));

        dispatch(resetResultDetails());
        const replaceUrl = (searchExpression: string) => {
            changeUrl(getState, searchPageType, navigate, searchExpression);
        };
        return dispatch(doSearchRequest(request, isSubmittingNewQuery, true, replaceUrl));
    };
};

export const searchSavedSearch = (request: SearchRequest, navigate: NavigateFunction) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const searchPageType: SearchPageType = SearchPageType.BASIC;
        const pitId = getState().search[searchPageType].pointInTime;
        if (pitId) {
            dispatch(doClosePointInTime(pitId));
        }
        // scroll to top before executing the task. Prevents the infinite scroll list from loading a huge amount of items
        scrollToTop(SEARCH_RESULTS_LIST_CLASS);
        dispatch(startSearchAction(searchPageType, request.query));
        dispatch(updateAllFilters(request.filters));
        dispatch(resetResultDetails());
        const replaceUrl = (searchExpression: string) => {
            changeUrl(getState, searchPageType, navigate, searchExpression);
        };
        return dispatch(doSearchRequest(request, true, true, replaceUrl));
    };
};

function shouldUseAutoCorrection(isUsingAutoCorrection: boolean, query: string) {
    return isUsingAutoCorrection && !isBlacklistedForAutoCorrection(query);
}

export const resetResultDetails = (): ThunkAction<void, State, unknown, SearchPageAction> => {
    return (dispatch: Dispatch) => {
        dispatch(setPreviewDocumentAction(undefined));
        dispatch(setPreviewDocumentWithHighlightsAction(undefined));
        dispatch(searchResultSetExpandedDocumentAction(SearchPageType.BASIC, undefined));
        dispatch(changeInDocumentQueryAction(undefined));
        dispatch(setCurrentPageNumberAction(1));
    };
};

export const changeUrl = (
    getState: GetState,
    searchPageType: SearchPageType,
    navigate: NavigateFunction,
    newSearchExpression = '',
): void => {
    const replaceSearchExpression: boolean = newSearchExpression !== '';
    const searchExpression: string = replaceSearchExpression
        ? newSearchExpression
        : getState().search[searchPageType].query;
    const sortField = getState().search[searchPageType].currentSortType.field;
    const facets: HibuFacetGroup[] = getState().filter.facets;
    const selectedFilters: HibuFacetFilter = getState().filter.selectedFilters;

    const openTab = getState().search[searchPageType].resultDetailsTab.openResultTab;
    const showFieldSpecificSearch = isShowFieldSpecificSearch(getState());
    const queryFields = getState().search[searchPageType].queryFields;
    const filterPersisted = getState().filter.filterPersisted;

    const encodedUrlPath: string = buildEncodedUrlPath(
        searchPageAttributesMap[searchPageType].route,
        searchExpression,
        sortField,
        facets,
        selectedFilters,
        openTab,
        showFieldSpecificSearch,
        queryFields,
        filterPersisted,
    );
    replaceSearchExpression ? navigate(encodedUrlPath, {replace: true}) : navigate(encodedUrlPath);
};

export const isShowFieldSpecificSearch = (state: DefaultRootState): boolean =>
    state.config.uiConfig.settings.toggles.showFieldSpecificSearch;
